/**
 * ActivityWidget.jsx
 * ===================
 * Widget Cockpit Dirigeant — Suivi vendeur (100% automatique).
 * Alimenté par les événements Homly (messagerie interne + RDV + comptes-rendus) — zéro saisie.
 * NB : les KPI de saisie manuelle (appels/relances) ont été retirés pour ne pas
 * imposer de tracking manuel aux agences (friction d'adoption). Réactivables en V2.
 */
import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import api from '../../lib/api';
import {
    MessageSquare, Clock, Bell, FileText, AlertTriangle,
} from 'lucide-react';

export default function ActivityWidget() {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState({});

    useEffect(() => {
        (async () => {
            try {
                const { data: res } = await api.get('/kpi/compute', {
                    params: { ids: 'seller_no_contact_14d,mandates_no_contact_7d,dormant_mandates,report_frequency' }
                });
                setData(res?.results || {});
            } catch (e) {
                // silent
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    if (loading) {
        return (
            <div className="rounded-2xl p-4 bg-card border border-white/10 space-y-2" data-testid="activity-widget-loading">
                <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-white/5 animate-pulse" />
                    <div className="h-3 w-40 rounded bg-white/5 animate-pulse" />
                </div>
                <div className="grid grid-cols-2 gap-2">
                    {[0, 1, 2, 3].map(i => <div key={i} className="h-14 rounded-lg bg-white/[0.03] animate-pulse" />)}
                </div>
            </div>
        );
    }

    const noContact14 = data.seller_no_contact_14d?.value ?? 0;
    const noContact7 = data.mandates_no_contact_7d?.value ?? 0;
    const dormant = data.dormant_mandates?.value ?? 0;
    const reportFreq = data.report_frequency?.value;

    const cnt = (v) => (v > 0 ? 'text-amber-400' : 'text-green-400');

    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="relative rounded-2xl overflow-hidden border border-cyan-500/20 bg-gradient-to-br from-cyan-500/10 via-card to-blue-500/5"
            data-testid="activity-widget"
        >
            <div className="relative p-4 sm:p-5">
                <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center shrink-0">
                        <MessageSquare className="w-5 h-5 text-white" />
                    </div>
                    <div className="min-w-0 flex-1">
                        <p className="text-[10px] text-cyan-400 font-bold uppercase tracking-wider">Suivi vendeur · automatique</p>
                        <p className="text-base font-bold text-foreground">
                            {noContact14 > 0
                                ? `${noContact14} vendeur${noContact14 > 1 ? 's' : ''} à relancer`
                                : 'Portefeuille bien suivi'}
                        </p>
                    </div>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
                    <Tile
                        label="Sans contact >14j"
                        value={noContact14}
                        sub="vendeurs"
                        color={cnt(noContact14)}
                        icon={AlertTriangle}
                    />
                    <Tile
                        label="Sans contact >7j"
                        value={noContact7}
                        sub="mandats"
                        color={cnt(noContact7)}
                        icon={Bell}
                    />
                    <Tile
                        label="Mandats dormants"
                        value={dormant}
                        sub="> 14j sans action"
                        color={cnt(dormant)}
                        icon={Clock}
                    />
                    <Tile
                        label="CR / sem / mandat"
                        value={reportFreq !== null && reportFreq !== undefined ? reportFreq : '—'}
                        sub="comptes-rendus"
                        color="text-foreground"
                        icon={FileText}
                    />
                </div>

                <p className="mt-1 flex items-center gap-1.5 text-[9px] text-muted-foreground">
                    <FileText className="w-3 h-3" />
                    Suivi alimenté automatiquement (messagerie, agenda, comptes-rendus). Aucune saisie manuelle.
                </p>
            </div>
        </motion.div>
    );
}

function Tile({ label, value, sub, color = 'text-foreground', icon: Icon }) {
    return (
        <div className="p-2.5 rounded-lg bg-white/[0.03] border border-white/10">
            <div className="flex items-center gap-1.5 mb-1">
                {Icon && <Icon className={`w-3 h-3 ${color}`} />}
                <p className="text-[9px] uppercase tracking-wider text-muted-foreground font-bold truncate">{label}</p>
            </div>
            <p className={`text-lg font-black leading-none ${color}`}>{value}</p>
            {sub && <p className="text-[9px] text-muted-foreground mt-1 truncate">{sub}</p>}
        </div>
    );
}
